import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { DeleteSessionButton } from "../components/DeleteSessionButton";
import { SessionConditions } from "../components/SessionConditions";
import { SessionPrivacyToggle } from "../components/SessionPrivacyToggle";
import {
  MAX_COMPARED_LAPS,
  TraceWorkspace,
  useChannelStack,
} from "../components/TraceWorkspace";
import { api } from "../lib/api";
import {
  formatBytes,
  formatDelta,
  formatDuration,
  formatLapTime,
  formatSpeedKph,
} from "../lib/format";
import { alignTrace, type AlignedLap } from "../lib/trace";
import type { Lap } from "../lib/types";

export function SessionDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const sessionId = id ?? "";

  const [selectedLaps, setSelectedLaps] = useState<number[]>([]);
  const { stack, toggleChannel, requestedChannels } = useChannelStack();

  // Already resolved and cached by the app shell; this only reads it.
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });

  const session = useQuery({
    queryKey: ["session", sessionId],
    queryFn: () => api.getSession(sessionId),
    // Ingest takes seconds, so poll until it settles.
    refetchInterval: (query) =>
      query.state.data?.status === "uploaded" || query.state.data?.status === "processing"
        ? 1_500
        : false,
  });

  const isReady = session.data?.status === "ready";

  const laps = useQuery({
    queryKey: ["laps", sessionId],
    queryFn: () => api.listLaps(sessionId),
    enabled: isReady,
  });

  const channels = useQuery({
    queryKey: ["channels"],
    queryFn: api.channels,
    enabled: isReady,
  });

  const traces = useQuery({
    queryKey: ["traces", sessionId, selectedLaps, requestedChannels],
    queryFn: () => api.getTraces(sessionId, selectedLaps, requestedChannels),
    enabled: isReady && selectedLaps.length > 0,
  });

  const aligned = useMemo(
    () => (traces.data ? alignTrace(traces.data) : null),
    [traces.data],
  );

  // Everything compares against the quickest lap on screen, which is also the lap whose
  // line the track map draws and whose corners the list shows.
  const reference = useMemo<AlignedLap | null>(() => {
    if (!aligned || aligned.laps.length === 0) return null;
    const lapTimes = new Map(
      (laps.data ?? []).map((lap) => [lap.lap_number, lap.lap_time_ms]),
    );
    const timed = aligned.laps.filter((lap) => lapTimes.get(lap.lap) != null);
    if (timed.length === 0) return aligned.laps[0];
    return timed.reduce((best, lap) =>
      (lapTimes.get(lap.lap) as number) < (lapTimes.get(best.lap) as number) ? lap : best,
    );
  }, [aligned, laps.data]);

  const fastestLapTime = useMemo(() => {
    const valid = (laps.data ?? []).filter((lap) => lap.is_valid && lap.lap_time_ms);
    if (valid.length === 0) return null;
    return Math.min(...valid.map((lap) => lap.lap_time_ms as number));
  }, [laps.data]);

  const sameTrack = useQuery({
    queryKey: ["same-track", sessionId],
    queryFn: () => api.sameTrackSessions(sessionId),
    enabled: isReady,
  });

  // Only sessions whose laps could actually be laid over this one's.
  const otherSessions = useMemo(() => {
    const step = session.data?.distance_step_m ?? null;
    return (sameTrack.data ?? []).filter(
      (candidate) =>
        candidate.id !== sessionId &&
        candidate.laps.length > 0 &&
        (step === null ||
          candidate.distance_step_m === null ||
          Math.abs(candidate.distance_step_m - step) < 1e-9),
    ).length;
  }, [sameTrack.data, session.data?.distance_step_m, sessionId]);

  // What a comparison across sessions starts from when no lap is picked yet: this
  // session's best, which is what it would be judged by anyway.
  const startingLap = useMemo(() => {
    const drawable = (laps.data ?? []).filter((lap) => lap.trace_available);
    const timed = drawable.filter((lap) => lap.is_valid && lap.lap_time_ms);
    if (timed.length > 0) {
      return timed.reduce((best, lap) =>
        (lap.lap_time_ms as number) < (best.lap_time_ms as number) ? lap : best,
      ).lap_number;
    }
    return drawable[0]?.lap_number ?? null;
  }, [laps.data]);

  function toggleLap(lapNumber: number) {
    setSelectedLaps((current) => {
      if (current.includes(lapNumber)) {
        return current.filter((value) => value !== lapNumber);
      }
      if (current.length >= MAX_COMPARED_LAPS) {
        return current;
      }
      return [...current, lapNumber].sort((a, b) => a - b);
    });
  }

  if (session.isPending) {
    return <p className="text-sm text-zinc-500">{t("session.loading")}</p>;
  }
  if (session.isError || !session.data) {
    return <p className="text-sm text-red-400">{t("session.notFound")}</p>;
  }

  const detail = session.data;
  // A teammate's session is readable but not editable, so the controls only make
  // sense on your own.
  const mine = me.data?.id === detail.owner_id;

  const lapRefs = (numbers: number[]) =>
    numbers.map((lap) => `${sessionId}:${lap}`).join(",");
  const compareLink = `/compare?laps=${lapRefs(selectedLaps)}&from=${sessionId}`;
  const acrossLaps =
    selectedLaps.length > 0 ? selectedLaps : startingLap !== null ? [startingLap] : [];
  const acrossLink = `/compare?laps=${lapRefs(acrossLaps)}&pick=mine&from=${sessionId}`;

  const lapTable = (
    <div>
      <h2 className="text-sm font-medium uppercase tracking-wider text-zinc-500">
        {t("session.laps")}
      </h2>

      {selectedLaps.length === 0 ? (
        <p className="mt-3 rounded-lg border border-dashed border-zinc-700 px-3 py-2 text-xs text-zinc-500">
          {t("session.pickHint")}
        </p>
      ) : (
        <Link
          to={compareLink}
          className="mt-3 flex items-center justify-center gap-2 rounded-lg border border-sky-500/60 bg-sky-500/10 px-3 py-2 text-sm font-medium text-sky-300 transition-colors hover:bg-sky-500/20"
        >
          {t("session.compare")}
          <span aria-hidden>→</span>
        </Link>
      )}

      {otherSessions > 0 && acrossLaps.length > 0 && (
        <Link
          to={acrossLink}
          className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2 text-xs text-zinc-400 transition-colors hover:border-sky-500/50 hover:text-zinc-200"
        >
          <span>
            {t(mine ? "session.sameTrack.others" : "session.sameTrack.yours", {
              count: otherSessions,
            })}
          </span>
          <span className="shrink-0 font-medium text-sky-300">
            {t("session.sameTrack.action")}
          </span>
        </Link>
      )}

      <div className="mt-4 overflow-hidden rounded-xl border border-zinc-800">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-left text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-3 py-2 font-medium">{t("session.columns.lap")}</th>
              <th className="px-3 py-2 text-right font-medium">
                {t("session.columns.time")}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t("session.columns.gap")}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t("session.columns.vmax")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {(laps.data ?? []).map((lap) => (
              <LapRow
                key={lap.id}
                lap={lap}
                colour={
                  aligned?.laps.find((candidate) => candidate.lap === lap.lap_number)
                    ?.colour
                }
                selected={selectedLaps.includes(lap.lap_number)}
                fastestLapTime={fastestLapTime}
                onToggle={() => toggleLap(lap.lap_number)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div>
      <Link to="/" className="text-sm text-zinc-500 hover:text-zinc-300">
        ← {t("session.back")}
      </Link>

      <div className="mt-3 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {detail.track_name ?? detail.original_filename}
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            {t("session.drivenBy", { name: detail.owner_name })}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* The lap table sits at the bottom of a long right-hand column, so the
              action it enables is repeated up here where actions are looked for. */}
          {isReady && selectedLaps.length > 0 && (
            <Link
              to={compareLink}
              className="rounded-lg bg-sky-500 px-3 py-1.5 text-sm font-medium text-zinc-950 transition-colors hover:bg-sky-400"
            >
              {t("session.compare")}
            </Link>
          )}
          {mine && (
            <>
              <SessionPrivacyToggle session={detail} />
              <DeleteSessionButton
                sessionId={sessionId}
                onDeleted={() => navigate("/")}
              />
            </>
          )}
        </div>
      </div>
      <dl className="mt-3 flex flex-wrap gap-x-8 gap-y-2 text-sm">
        <Fact label={t("session.facts.car")} value={detail.car_name ?? "—"} />
        <Fact label={t("session.facts.class")} value={detail.car_class ?? "—"} />
        <Fact label={t("session.facts.laps")} value={String(detail.lap_count)} />
        <Fact
          label={t("session.facts.duration")}
          value={formatDuration(detail.duration_ms)}
        />
        <Fact
          label={t("session.facts.traceSize")}
          value={formatBytes(detail.trace_bytes)}
        />
        <Fact
          label={t("session.facts.resolution")}
          value={
            detail.distance_step_m
              ? t("session.facts.grid", { step: detail.distance_step_m })
              : "—"
          }
        />
      </dl>

      {isReady && <SessionConditions session={detail} laps={laps.data ?? []} />}

      {detail.status === "failed" && (
        <div className="mt-6 rounded-lg border border-red-900/60 bg-red-950/40 px-4 py-3 text-sm text-red-200">
          <p className="font-medium">{t("session.ingestFailed")}</p>
          <p className="mt-1 font-mono text-xs leading-relaxed text-red-200/80">
            {detail.status_detail ?? t("session.noDetail")}
          </p>
        </div>
      )}

      {(detail.status === "uploaded" || detail.status === "processing") && (
        <p className="mt-6 text-sm text-sky-400">{t("session.processing")}</p>
      )}

      {isReady && (
        <div className="mt-8">
          {selectedLaps.length === 0 || !aligned ? (
            <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_26rem]">
              <div className="rounded-xl border border-zinc-800">
                <p className="py-16 text-center text-sm text-zinc-500">
                  {traces.isPending && selectedLaps.length > 0
                    ? t("session.traceLoading")
                    : traces.isError
                      ? t("session.traceError")
                      : t("session.selectHint", { count: MAX_COMPARED_LAPS })}
                </p>
              </div>
              <aside>{lapTable}</aside>
            </div>
          ) : (
            <TraceWorkspace
              aligned={aligned}
              distanceStep={traces.data?.distance_step_m ?? 0}
              channels={channels.data ?? []}
              stack={stack}
              onToggleChannel={toggleChannel}
              reference={reference}
              selectionKey={`${sessionId}:${selectedLaps.join(",")}`}
            >
              {lapTable}
            </TraceWorkspace>
          )}
        </div>
      )}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-zinc-500">{label}</dt>
      <dd className="mt-0.5 text-zinc-200">{value}</dd>
    </div>
  );
}

interface LapRowProps {
  lap: Lap;
  colour?: string;
  selected: boolean;
  fastestLapTime: number | null;
  onToggle: () => void;
}

function LapRow({ lap, colour, selected, fastestLapTime, onToggle }: LapRowProps) {
  const { t } = useTranslation();
  const isFastest = lap.is_valid && lap.lap_time_ms === fastestLapTime;

  return (
    <tr
      onClick={onToggle}
      className={[
        "cursor-pointer transition-colors",
        selected ? "bg-sky-500/10" : "hover:bg-zinc-900/40",
      ].join(" ")}
    >
      <td className="px-3 py-2">
        <span className="flex items-center gap-2">
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: selected && colour ? colour : "transparent" }}
          />
          <span className="tabular-nums">{lap.lap_number}</span>
          {lap.is_out_lap && (
            <span className="text-[10px] uppercase text-zinc-500">
              {t("session.flags.out")}
            </span>
          )}
          {lap.is_in_lap && !lap.is_out_lap && (
            <span className="text-[10px] uppercase text-zinc-500">
              {t("session.flags.in")}
            </span>
          )}
          {lap.is_valid === false && !lap.is_out_lap && !lap.is_in_lap && (
            <span className="text-[10px] uppercase text-amber-500">
              {t("session.flags.invalid")}
            </span>
          )}
        </span>
      </td>
      <td
        className={[
          "px-3 py-2 text-right tabular-nums",
          isFastest ? "font-medium text-fuchsia-400" : "text-zinc-200",
        ].join(" ")}
      >
        {formatLapTime(lap.lap_time_ms)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-zinc-500">
        {formatDelta(lap.lap_time_ms, fastestLapTime)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-zinc-400">
        {formatSpeedKph(lap.max_speed_kph)}
      </td>
    </tr>
  );
}
